import firebase from 'firebase/compat/app';

import { GroupId } from '../models/Group'
import { FreshSong, Song, SongId, SongRef } from '../models/Song'
import { SpecifiedDate, Timestamp } from '../models/Timeline'
import { findHashtags, trimToTen } from '../util/sugar'

import { DirectoryService } from './DirectoryService'
import ObserverService from './ObserverService'
import { getViewer, Viewer } from './Viewer'
import isEmpty from 'lodash.isempty'
import { DEFAULT_SONG_PRIVACY, ENABLE_GROUPS } from '../config/env';
const firestore=firebase.firestore

// The `in`-filter disjunction budget: Firestore allows at most 30 combined
// disjunctions per query, and group feeds pair `groupId in [...]` with
// `privacy in ['public','member']` (×2), so groups are capped at 15.
const GROUP_IN_LIMIT = 15

export class SongService {

  // Visibility scope the newest-song listeners are currently registered for.
  private static observedScope: string | null = null

  /**
   * Apply the privacy filter that makes an UN-grouped list query provable
   * under the Firestore rules:
   *  - staff: no filter (isStaff() passes rules for any doc)
   *  - signed-in on a group-less instance: public + member (rules treat
   *    "member" as any signed-in user while configuration/app.groupPrivacy
   *    is off, which is exactly the ENABLE_GROUPS=false deployments)
   *  - everyone else (anonymous, or signed-in on a groups instance querying
   *    outside a group scope): public only — "member" there belongs to a
   *    group the query doesn't pin, so the rules can't prove it.
   */
  static applyGlobalPrivacy = (query: firebase.firestore.Query, viewer: Viewer) => {
    if (viewer.staff) return query
    if (viewer.authed && !ENABLE_GROUPS) {
      return query.where('privacy', 'in', ['public', 'member'])
    }
    return query.where('privacy', '==', 'public')
  }

  /**
   * Base query for a group-scoped feed. Filtering on the scalar `groupId`
   * (equality/in) is what lets the rules resolve the group membership get();
   * non-staff additionally pin the readable tiers.
   */
  static groupFeedQuery = (groupIds: string[], viewer: Viewer) => {
    const db = firestore()
    let query = db
      .collection('songs')
      .where('groupId', 'in', [...groupIds].sort().slice(0, GROUP_IN_LIMIT))
    if (!viewer.staff) {
      query = query.where('privacy', 'in', ['public', 'member'])
    }
    return query
  }

  /**
   * The identity of a feed's visibility scope. Fetches whose scope key
   * differs must not share a timeline/cursor — useSongs resets the timeline
   * when this changes so `oldest()` pagination never develops gaps.
   */
  static feedScopeKey = (groups: GroupId[]): string => {
    const viewer = getViewer()
    const grouped = ENABLE_GROUPS && viewer.authed && groups.length > 0
    return JSON.stringify({
      grouped,
      groups: grouped ? [...groups].sort().slice(0, GROUP_IN_LIMIT) : [],
      authed: viewer.authed,
      staff: viewer.staff,
    })
  }

  static fetchSongsForRegion = (authorIds: string[], pageSize: number, offset: number = 0): Promise<Song[]> => {
    if (!authorIds || authorIds.length === 0) return Promise.resolve([]);
    const authorBatch = authorIds.slice(0, 10); // array-contains-any limit
    const viewer = getViewer()
    return new Promise((resolve, reject) => {
      const db = firestore()
      const buildQuery = () => SongService.applyGlobalPrivacy(
        db.collection('songs').where('authors', 'array-contains-any', authorBatch),
        viewer,
      )
        .orderBy('updatedAt', 'desc')
        .limit(pageSize)

      if (offset > 0) {
        SongService.applyGlobalPrivacy(
          db.collection('songs').where('authors', 'array-contains-any', authorBatch),
          viewer,
        )
          .orderBy('updatedAt', 'desc')
          .limit(offset)
          .get()
          .then((snapshot) => {
            const lastVisible = snapshot.docs[snapshot.docs.length - 1];
            buildQuery().startAfter(lastVisible)
              .get()
              .then((querySnapshot) => {
                resolve(querySnapshot.docs.map((d) => SongService.StronglyTypeSongData(d)));
              })
              .catch(reject)
          })
          .catch(reject)
      } else {
        buildQuery().get()
          .then((querySnapshot) => {
            resolve(querySnapshot.docs.map((d) => SongService.StronglyTypeSongData(d)));
          })
          .catch(reject)
      }
    })
  }

  static observe(
    groups: GroupId[],
    onFreshSong: (song: Song) => void,
    onRevokeSong: (songId: string) => void,
  ) {
    // Only require groups when groups are enabled
    if (ENABLE_GROUPS && groups.length === 0) {
      return
    }
    const viewer = getViewer()
    // Re-registering for the same scope is a no-op; a NEW scope replaces the
    // old listeners (ObserverService.remove unsubscribes them) so the
    // observer can never inject songs the current feed scope couldn't fetch.
    const scope = SongService.feedScopeKey(groups)
    const observers = ObserverService.please()
    if (SongService.observedScope === scope && observers.exist('songs')) {
      return
    }
    observers.remove('songs')
    observers.remove('songs-revoked')
    SongService.observedScope = scope
    const db = firestore()

    // The listener MUST use the same constraints as fetchSome/fetchMore for
    // this scope — both for rules provability and so the cached timeline and
    // its cursor only ever contain songs this scope's queries return.
    const buildQuery = () => {
      const base = ENABLE_GROUPS && viewer.authed && groups.length > 0
        ? SongService.groupFeedQuery(groups, viewer)
        : SongService.applyGlobalPrivacy(db.collection('songs'), viewer)
      return base.orderBy('updatedAt', 'desc').limit(1)
    }

    const revokeWatcher = buildQuery()
      .onSnapshot((songQuery) => {
        songQuery.docChanges().forEach(function (change) {
          if (change.type === 'removed') {
            // Lets make sure the song is actually not there...
            SongService.fetch(change.doc.id).catch(() => {
              onRevokeSong(change.doc.id)
            })
          }
        })
      })

    const watcher = buildQuery()
      .onSnapshot((songQuery) => {
        songQuery.docChanges().forEach(function (change) {
          if (change.type === 'added') {
            const freshSong = {
              ...change.doc.data(),
              id: change.doc.id,
            } as Song
            onFreshSong(freshSong)
          }
          if (change.type === 'modified') {
            const freshSong = {
              ...change.doc.data(),
              id: change.doc.id,
            } as Song
            onFreshSong(freshSong)
          }
        })

        for (const songDoc of songQuery.docs) {
          const song = SongService.StronglyTypeSongData(songDoc)
          onFreshSong(song)
        }
      })

    observers.add('songs', watcher)
    observers.add('songs-revoked', revokeWatcher)
  }

  // Observe all songs without group filtering (for when ENABLE_GROUPS is false)
  static observeAll(
    onFreshSong: (song: Song) => void,
    onRevokeSong: (songId: string) => void,
  ) {
    SongService.observe([], onFreshSong, onRevokeSong)
  }

  // Fetch latest songs without group filtering (for when ENABLE_GROUPS is false)
  static fetchSomeAll = (limit: number, publicOnly = false): Promise<Song[]> => {
    return new Promise((resolve, reject) => {
      const db = firestore()
      const viewer = getViewer()
      const base = publicOnly
        ? db.collection('songs').where('privacy', '==', 'public')
        : SongService.applyGlobalPrivacy(db.collection('songs'), viewer)
      const query: any = base.orderBy('updatedAt', 'desc').limit(limit)
      query
        .get()
        .then((querySnapshot: any) => {
          console.log('fetchSomeAll got', querySnapshot.docs.length, 'songs')
          const songBatch = querySnapshot.docs.map((songDoc: any) => {
            return SongService.StronglyTypeSongData(songDoc)
          })
          resolve(songBatch)
        })
        .catch((e: any) => {
          console.error('fetchSomeAll error', e)
          reject(e)
        })
    })
  }

  // Fetch older songs without group filtering (for when ENABLE_GROUPS is false)
  // timestamp = oldest song we have, fetch songs older than that
  static fetchMoreAll = (
    limit: number,
    timestamp: Timestamp,
    publicOnly = false,
  ): Promise<Song[]> => {
    if (timestamp.seconds === -1) {
      return SongService.fetchSomeAll(limit, publicOnly)
    }
    return new Promise((resolve, reject) => {
      const db = firestore()
      const viewer = getViewer()
      const base = publicOnly
        ? db.collection('songs').where('privacy', '==', 'public')
        : SongService.applyGlobalPrivacy(db.collection('songs'), viewer)
      const query: any = base.orderBy('updatedAt', 'desc').startAfter(timestamp).limit(limit)
      query
        .get()
        .then((querySnapshot: any) => {
          console.log('fetchMoreAll got', querySnapshot.docs.length, 'songs older than', timestamp)
          const songBatch = querySnapshot.docs.map((songDoc: any) =>
            SongService.StronglyTypeSongData(songDoc),
          )
          resolve(songBatch)
        })
        .catch(reject)
    })
  }

  // Fetch songs on date without group filtering (for when ENABLE_GROUPS is false)
  static fetchSongsOnDateAll = (date: string) => {
    return new Promise<Song[]>((resolve, reject) => {
      const db = firestore()
      const dayBegin = new Date(date + 'T00:00:00')
      const dayEnd = new Date(date + 'T23:59:59')

      SongService.applyGlobalPrivacy(db.collection('songs'), getViewer())
        .orderBy('updatedAt')
        .where('updatedAt', '>', dayBegin)
        .where('updatedAt', '<', dayEnd)
        .get()
        .then((querySnapshot) => {
          const songBatch = querySnapshot.docs.map((songDoc) => {
            return SongService.StronglyTypeSongData(songDoc)
          })
          resolve(songBatch)
        })
        .catch(reject)
    })
  }

  static toRef(song: Song): SongRef {
    return {
      id: song.id,
      updatedAt: song.updatedAt,
      comments: song.comments || [],
    } as SongRef
  }

  // Fetch every song that points at `songId` via its `original` field (its
  // revisions), plus the original song itself. Runs one provable query per
  // readable slice (public / own / legacy-member) and merges, because a
  // single unfiltered `original ==` query is denied by the privacy rules.
  static fetchRevisions(songId: string) {
    return new Promise<Song[]>(async (resolve, reject) => {
      try {
        const db = firestore()
        const viewer = getViewer()
        const base = db.collection('songs').where('original', '==', songId)
        const queries: firebase.firestore.Query[] = []
        if (viewer.staff) {
          queries.push(base)
        } else {
          queries.push(base.where('privacy', '==', 'public'))
          if (viewer.uid) queries.push(base.where('ownerId', '==', viewer.uid))
          if (viewer.authed && !ENABLE_GROUPS) {
            queries.push(base.where('privacy', '==', 'member'))
          }
        }
        const snapshots = await Promise.all(queries.map((q) => q.get()))
        const byId: Record<string, Song> = {}
        snapshots.forEach((snap) =>
          snap.docs.forEach((d) => {
            byId[d.id] = SongService.StronglyTypeSongData(d)
          }),
        )
        // Include the original itself when it is readable; a permission
        // denial here must not sink the revisions we already have.
        await SongService.fetch(songId)
          .then((song) => {
            if (song.id) byId[song.id] = song
          })
          .catch(() => undefined)
        const songs = Object.values(byId).sort((a, b) => {
          const aTime = (a.updatedAt as any)?.seconds ?? 0
          const bTime = (b.updatedAt as any)?.seconds ?? 0
          return bTime - aTime
        })
        resolve(songs)
      } catch (e) {
        reject(e)
      }
    })
  }

  // Create a new song that is a revision of `original`. Stamps the original
  // with its own id in the `original` field so the original + every revision
  // share the same `original` key, then transmits the new revision.
  static revise = async (original: string, song: FreshSong): Promise<Song> => {
    const { description, authors, ownerId, title, media } = song
    const db = firestore()
    await db.collection('songs').doc(original).update({ original })
    return SongService.transmit({
      description,
      media,
      authors,
      ownerId,
      title,
      original,
      updatedAt: firestore.FieldValue.serverTimestamp(),
    } as FreshSong)
  }

  static transmit = async (freshSong: FreshSong): Promise<Song> => {
    const db = firestore()
    const songsDB = db.collection('songs')
    const artists = await Promise.all(
      freshSong.authors.map(async (artistId) =>
        DirectoryService.fetch(artistId),
      ),
    )

    freshSong.artists = {}

    for (const artist of artists) {
      let { id, ownerId, alias, avatar } = artist
      if (avatar === undefined) avatar = ''
      // Firestore rejects explicit `undefined` field values; a profile that
      // never set a display name would otherwise fail the whole transmit.
      if (alias === undefined) alias = ''
      freshSong.artists[id] = { id, ownerId, alias, avatar }
      // Precedence: the transmitter's per-song choice, then the artist
      // profile's privacy posture, then the instance default. (The group's
      // defaultPrivacy is applied upstream by the transmit form/store.)
      freshSong.privacy = freshSong.privacy || artist.privacy || DEFAULT_SONG_PRIVACY
    }

    // Always write an explicit tier: the rules reject a new song without a
    // valid `privacy` and treat an absent one as restricted (S06).
    freshSong.privacy = freshSong.privacy || DEFAULT_SONG_PRIVACY

    const tags = findHashtags(freshSong.description)

    if (tags) freshSong.tags = tags
    if (freshSong.title === ' ') freshSong.title = 'Untitled'

    // Keep the scalar groupId mirror in lockstep with groups[0]; the privacy
    // rules and every group feed query key off the scalar.
    if (freshSong.groups && freshSong.groups.length > 0) {
      freshSong.groupId = freshSong.groups[0]
    }

    // The submission number used to come from a client-side count of the
    // whole songs collection, which the privacy rules now (correctly) deny
    // to non-staff. The assignSubmissionNumber Cloud Function stamps it
    // server-side on create instead.

    // Song ids used to come from pre-creating an empty threads doc. Thread
    // writes are now gated on the parent song existing and being readable
    // (S02/S06), so just allocate an id; the thread doc is created lazily
    // with the first comment (CommentThreadService.transmit).
    const { id } = songsDB.doc()

    return new Promise((resolve, reject) => {
      const songRef = songsDB.doc(id)
      songRef.set(freshSong).then(() => {
        songRef
          .get()
          .then((songDoc) => {
            if (songDoc !== undefined) {
              // Search indexing is done by the syncSongSearch Cloud Function.
              resolve(SongService.StronglyTypeSongData(songDoc))
            }
          })
          .catch(reject)
      })
    })
  }

  static updateSong = async (
    id: SongId,
    updatedSong: FreshSong,
    updateArtists: boolean = true,
  ) => {
    const freshSong = { ...updatedSong }

    !freshSong.art && delete freshSong.art

    if (updateArtists) {
      freshSong.artists = {}

      const artists = await Promise.all(
        freshSong.authors.map(async (artistId) =>
          DirectoryService.fetch(artistId),
        ),
      )

      for (const artist of artists) {
        const { id, ownerId, alias, avatar } = artist

        freshSong.artists[id] = { id, ownerId, alias, avatar: avatar || '' }
        // Same precedence as transmit(): a per-song privacy on the edited
        // song wins over the artist profile's posture.
        freshSong.privacy = updatedSong.privacy || artist.privacy || DEFAULT_SONG_PRIVACY
      }
    }

    // Keep the scalar groupId mirror in lockstep with groups[0].
    if (freshSong.groups && freshSong.groups.length > 0) {
      freshSong.groupId = freshSong.groups[0]
    }

    return new Promise<FreshSong>((resolve, reject) => {
      const db = firestore()
      if (id) {
        // Firestore { merge: true } only updates fields present in the
        // payload — fields not in the payload are left untouched. When the
        // caller has dropped `art` (user clicked Remove in the edit form),
        // we need to explicitly tell Firestore to remove that field via
        // FieldValue.delete(); otherwise the old art persists.
        const writePayload: { [key: string]: any } = { ...freshSong }
        if (!freshSong.art) {
          writePayload.art = firebase.firestore.FieldValue.delete()
        }

        db.collection('songs')
          .doc(id)
          .set(writePayload, { merge: true })
          .then(() => {
            resolve(freshSong)
            return freshSong
          })
          .catch((err) => {
            reject(err)
          })
      } else {
        reject()
      }
    })
  }

  static delete = (id: string): Promise<void> => {
    return new Promise<void>(async (resolve) => {
      const db = firestore()
      if (!id || id === '') {
        return
      }
      const { original } = await SongService.fetch(id)
      await db
        .collection('songs')
        .doc(id)
        .delete()
        .then(async () => {
          // syncSongSearch removes the search record server-side.
          resolve()
        })
      if (original && original !== id) {
        db.collection('songs')
          .where('original', '==', original)
          .get()
          .then((queryResult) => {
            if (queryResult.size === 1) {
              db.collection('songs')
                .doc(original)
                .update({ original: firestore.FieldValue.delete() })
            }
          })
      }
    })
  }

  static getFileExtension = (url: string) => {
    return new Promise<string>(async (resolve, reject) => {
      const songRef = firebase.storage().refFromURL(url)

      const ext = await songRef
        .getMetadata()
        .then()
        .then((data) => data.name.split('.').slice(-1)[0])
        .catch((err) => {
          console.error(err)
          reject(err)
        })

      resolve(ext as any)
      return ext
    })
  }

  static fetch = (id: string) => {
    return new Promise<Song>((resolve, reject) => {
      const db = firestore()
      if (!id || id === '') {
        reject()
      } else {
        db.collection('songs')
          .doc(id)
          .get()
          .then((songDoc) => {
            if (!songDoc.exists) {
              resolve({} as Song)
            }
            resolve(SongService.StronglyTypeSongData(songDoc))
          })
          .catch(reject)
      }
    })
  }

  static fetchSome = (limit: number, groupIds: string[]): Promise<Song[]> => {
    return new Promise((resolve, reject) => {
      const db = firestore()
      const viewer = getViewer()
      // Group scope requires a signed-in viewer — the rules can only grant
      // "member" songs to authenticated group members. Everyone else gets the
      // provable public timeline.
      const base = ENABLE_GROUPS && viewer.authed && groupIds.length > 0
        ? SongService.groupFeedQuery(groupIds, viewer)
        : SongService.applyGlobalPrivacy(db.collection('songs'), viewer)
      const query = base.orderBy('updatedAt').limitToLast(limit)

      query.get()
        .then((querySnapshot) => {
          const songBatch = querySnapshot.docs.map((songDoc) => {
            return SongService.StronglyTypeSongData(songDoc)
          })
          resolve(songBatch)
        })
        .catch(reject)
    })
  }

  static fetchSongsOnDate = (groups: string[], date: string) => {
    return new Promise<Song[]>((resolve, reject) => {
      const db = firestore()
      const viewer = getViewer()
      const dayBegin = new Date(date + 'T00:00:00')
      const dayEnd = new Date(date + 'T23:59:59')
      const base = ENABLE_GROUPS && viewer.authed && groups.length > 0
        ? SongService.groupFeedQuery(groups, viewer)
        : SongService.applyGlobalPrivacy(db.collection('songs'), viewer)

      base
        .orderBy('updatedAt')
        .where('updatedAt', '>', dayBegin)
        .where('updatedAt', '<', dayEnd)
        .get()
        .then((querySnapshot) => {
          const songBatch = querySnapshot.docs.map((songDoc) => {
            return SongService.StronglyTypeSongData(songDoc)
          })
          resolve(songBatch)
        })
        .catch(reject)
    })
  }

  static fetchMore = (
    groups: string[],
    limit: number,
    timestamp: Timestamp,
  ): Promise<Song[]> => {
    if (timestamp.seconds === -1) {
      return SongService.fetchSome(limit, groups)
    }
    return new Promise((resolve, reject) => {
      const db = firestore()
      const viewer = getViewer()
      const base = ENABLE_GROUPS && viewer.authed && groups.length > 0
        ? SongService.groupFeedQuery(groups, viewer)
        : SongService.applyGlobalPrivacy(db.collection('songs'), viewer)
      const query = base
        .orderBy('updatedAt')
        .endBefore(timestamp)
        .limitToLast(limit)

      query.get()
        .then((querySnapshot) => {
          const songBatch = querySnapshot.docs.map((songDoc) =>
            SongService.StronglyTypeSongData(songDoc),
          )
          resolve(songBatch)
        })
        .catch(reject)
    })
  }

  static fetchSongsForAssignment = (assignmentId: string, groupId?: string): Promise<Song[]> => {
    return new Promise((resolve, reject) => {
      const db = firestore()
      const viewer = getViewer()
      let query = db.collection('songs').where('assignment', '==', assignmentId)
      if (!viewer.staff) {
        if (ENABLE_GROUPS && viewer.authed && groupId) {
          // Pinning the assignment's group makes the "member" tier provable
          // for group members under the privacy rules.
          query = query
            .where('groupId', '==', groupId)
            .where('privacy', 'in', ['public', 'member'])
        } else {
          query = SongService.applyGlobalPrivacy(query, viewer)
        }
      }
      query.orderBy("updatedAt","desc")
        .get()
        .then((querySnapshot) => {
          const songBatch = querySnapshot.docs.map((songDoc) =>
            SongService.StronglyTypeSongData(songDoc),
          )
          resolve(songBatch)
        })
        .catch((e)=>{
console.log(e)
reject(e);
        })
    })
  }

  /**
   * Fetch some songs with a particular hashtag
   *
   * @param tag the hashtag to query
   * @param limit how many songs to get
   */
  static fetchSongsWithHashtag = (tag: string, groups: string[]) => {
    return new Promise<Song[]>(async (resolve, reject) => {
      try {
        const db = firestore()
        const viewer = getViewer()
        const base = db.collection('songs').where('tags', 'array-contains', tag)
        const queries: firebase.firestore.Query[] = []
        if (viewer.staff) {
          queries.push(base)
        } else {
          queries.push(SongService.applyGlobalPrivacy(base, viewer))
          if (ENABLE_GROUPS && viewer.authed && groups.length > 0) {
            // Member-tier songs from the viewer's own groups — provable
            // because the groupId pin lets rules check membership.
            queries.push(
              base
                .where('groupId', 'in', [...groups].sort().slice(0, GROUP_IN_LIMIT))
                .where('privacy', '==', 'member'),
            )
          }
        }
        const snapshots = await Promise.all(
          queries.map((q) => q.orderBy('updatedAt', 'desc').limit(10).get()),
        )
        const byId: Record<string, Song> = {}
        snapshots.forEach((snap) =>
          snap.docs.forEach((d) => {
            byId[d.id] = SongService.StronglyTypeSongData(d)
          }),
        )
        const songBatch = Object.values(byId)
          .sort(
            (a, b) =>
              ((b.updatedAt as any)?.seconds ?? 0) -
              ((a.updatedAt as any)?.seconds ?? 0),
          )
          .slice(0, 10)
        resolve(songBatch)
      } catch (e) {
        reject(e)
      }
    })
  }

  // fetchSongsWithHashtagInComments was removed (S06): it listed
  // /threads by `tags array-contains`, which the rules cannot prove now that
  // a thread is exactly as readable as its parent song (it would be denied
  // wholesale), it had no callers, and no client ever writes thread tags.
  // Song-level hashtags are still served by fetchSongsWithHashtag.

  /**
   * Fetch some songs with a particular hashtag
   *
   * @param tag the hashtag to query
   */
  static sortSongs = (assignments: string[],groups:string[]) => {
    return new Promise<SongRef[]>(async (resolve, reject) => {
      try {
        const db = firebase.firestore()
        const viewer = getViewer()
        const base: firebase.firestore.Query = db.collection('songs')
        const toRefs = (snapshots: firebase.firestore.QuerySnapshot[]): SongRef[] => {
          const ids = new Set<string>()
          snapshots.forEach((snap) => snap.docs.forEach((d) => ids.add(d.id)))
          const updatedAt: Timestamp = { seconds: 0, nanoseconds: 0 }
          return Array.from(ids).map((id) => ({ id, updatedAt }))
        }

        if (assignments && assignments.length) {
          const withAssignments = base.where('assignment', 'in', assignments.slice(0, 10))
          const queries: firebase.firestore.Query[] = []
          if (viewer.staff) {
            queries.push(withAssignments)
          } else {
            // Public slice across every group.
            queries.push(SongService.applyGlobalPrivacy(withAssignments, viewer))
            // Member-tier songs are provable one pinned group at a time — a
            // `groupId in [...]` here would blow the 30-disjunction budget
            // (10 assignments × groups × tiers), so run one equality-pinned
            // query per group and merge.
            if (ENABLE_GROUPS && viewer.authed && groups && groups.length) {
              ;[...groups].sort().slice(0, GROUP_IN_LIMIT).forEach((groupId) => {
                queries.push(
                  withAssignments
                    .where('groupId', '==', groupId)
                    .where('privacy', '==', 'member'),
                )
              })
            }
          }
          const snapshots = await Promise.all(queries.map((q) => q.get()))
          resolve(toRefs(snapshots))
          return
        }

        const query = groups && groups.length && viewer.authed && ENABLE_GROUPS
          ? SongService.groupFeedQuery(groups, viewer)
          : SongService.applyGlobalPrivacy(base, viewer)
        const snapshot = await query.get()
        resolve(toRefs([snapshot]))
      } catch (e) {
        reject(e)
      }
    })
  }
  

  static fetchSongsByParams = (
    groups: string[],
    assignments: string[],
    dateOrder: string,
    isShowLowComments: boolean,
    specifiedDate: SpecifiedDate,
    isRandomSongs: boolean,
    fetchMore: boolean,
    lastVisibleSong: SongId,
  ) => {
    return new Promise<Song[]>(async (resolve, reject) => {
      const db = firestore()
      console.log( groups,
        assignments,
        dateOrder,
        isShowLowComments,
        specifiedDate,
        isRandomSongs,
        fetchMore,
        lastVisibleSong)
      const songsDoc = db.collection('songs')

      // Get all sort fields and turn them into fields for firebase
      const isSpecifiedDate = dateOrder === 'specified'
      const sortedByDate = dateOrder === 'new' ? 'desc' : 'asc'

      // Define the documents by which we will search
      const currentDocument = isEmpty(assignments)
        ? songsDoc.where('groups', 'array-contains-any', trimToTen(groups))
        : songsDoc.where('assignment', 'in', assignments)

      // We divide the document in half depending on the song random id.
      // Then we determine (depending on the last received song) from which part of the part we will receive songs

      // Choose which method to sort this data
      const specifiedByDate = currentDocument
        .where(
          'updatedAt',
          '<=',
          new Date(specifiedDate.year, specifiedDate.month + 1, 0),
        )
        .where(
          'updatedAt',
          '>=',
          new Date(specifiedDate.year, specifiedDate.month),
        )

      const orderedSongs = isShowLowComments
        ? currentDocument.where('commentsNumber', '<', 3)
        : currentDocument.orderBy('updatedAt', sortedByDate)
      const currentSongsBySortType = isSpecifiedDate
        ? specifiedByDate
        : orderedSongs

      // Depending on the type of action, we download the first 10 songs,
      // or the next 10 based on the last downloaded song in the collection
      const lastVisibleItem =
        !isEmpty(lastVisibleSong) && (await songsDoc.doc(lastVisibleSong).get())

      const fetch = () => currentSongsBySortType.limit(10)
      const fetchMoreSongs = () =>
        currentSongsBySortType.startAfter(lastVisibleItem).limit(10)
      const typeOfFetch = fetchMore ? fetchMoreSongs() : fetch()

      typeOfFetch
        .get()
        .then(async (querySnapshot) => {
          const songsData: Song[] = []

          await querySnapshot.docs.forEach((songDoc) =>
            songsData.push(SongService.StronglyTypeSongData(songDoc)),
          )

          // It is necessary in order to switch the receipt of random songs to the second part,
          // if we received all of them from the first part

         return resolve(songsData)
        })
        .catch((err) => {
          console.error(err, 'filtered songs are empty')
          return reject()
        })
    })
  }

  static StronglyTypeSongData = (
    songDoc:
      | firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>
      | firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>,
  ) => {
    return { ...songDoc.data(), id: songDoc.id } as Song
  }
}
